import { useContext } from "react";
import { cleanDoubleSlashes, withQuery } from "ufo";

import { directusContext } from "~/shared/utils/directus/directus.provider";

export type ImageParams = {
  format?:
    | "jpg"
    | "jpeg"
    | "png"
    | "gif"
    | "webp"
    | "avif"
    | "svg"
    | "bmp"
    | "ico"
    | "tiff";
  height?: number;
  width?: number;
  fit?: "cover" | "contain" | "inside" | "outside";
  quality?: number;
  z?: string;
};

type UseDirectusOutput = {
  getDirectusImage: (imageId: ID, params?: ImageParams) => string;
};

const DEFAULT_RASTER_IMAGE_QUALITY = 65;
const RASTER_IMAGE_FORMATS = new Set<ImageParams["format"]>([
  "jpg",
  "jpeg",
  "png",
  "webp",
  "avif",
]);

const getDirectusImageUrl = (
  cmsPath: string,
  imageId: ID,
  params?: ImageParams,
): string => {
  const mergedParams: ImageParams = { format: "webp", ...params, z: ".img" };

  if (
    mergedParams.quality === undefined &&
    RASTER_IMAGE_FORMATS.has(mergedParams.format)
  ) {
    mergedParams.quality = DEFAULT_RASTER_IMAGE_QUALITY;
  }

  const queryParams = Object.fromEntries(
    Object.entries(mergedParams).filter(([key, value]) => {
      if (value == null) return false;
      if (
        (key === "width" || key === "height") &&
        typeof value === "number" &&
        value <= 0
      ) {
        return false;
      }
      return true;
    }),
  );

  return cleanDoubleSlashes(
    withQuery(`${cmsPath}/cms-assets/${imageId}`, queryParams),
  );
};

export const useDirectus = (): UseDirectusOutput => {
  const context = useContext(directusContext);
  if (!context) {
    throw new Error("Context can't be null, please add directus provider");
  }

  const cmsPath = context.url;
  return {
    getDirectusImage: (imageId, params) => {
      return getDirectusImageUrl(cmsPath, imageId, params);
    },
  };
};
